import { World } from '@ecs/World';
import { MovementSystem } from '@systems/MovementSystem';
import { LifetimeSystem } from '@systems/LifetimeSystem';
import { PureRenderSystem } from '@renderSystems/PureRenderSystem';
import { InputSystem } from '@systems/InputSystem';
import { StandardWeaponSystem } from '@systems/StandardWeaponSystem';
import { RailgunWeaponSystem } from '@systems/RailgunWeaponSystem';
import { TorpedoWeaponSystem } from '@systems/TorpedoWeaponSystem';
import { TorpedoSystem } from '@systems/TorpedoSystem';
import { PointDefenseSystem } from '@systems/PointDefenseSystem';
import { EnergyRegenSystem } from '@systems/EnergyRegenSystem';
import { ShieldSystem } from '@systems/ShieldSystem';
import { EnergyUISystem } from '@renderSystems/EnergyUISystem';
import { HunterAISystem } from '@systems/HunterAISystem';
import { SniperAISystem } from '@systems/SniperAISystem';
import { CollisionLogicSystem } from '@systems/CollisionLogicSystem';
import { CollisionEffectSystem } from '@systems/CollisionEffectSystem';
import { GameStateSystem } from '@systems/GameStateSystem';
import { ParticleSystem } from '@renderSystems/ParticleSystem';
import { SeparationSystem } from '@systems/SeparationSystem';
import { TargetingLogicSystem } from '@systems/TargetingLogicSystem';
import { WeaponRangeLogicSystem } from '@renderSystems/WeaponRangeLogicSystem';
import { HealthDisplayLogicSystem } from '@renderSystems/HealthDisplayLogicSystem';
import { ShieldLogicSystem } from '@systems/ShieldLogicSystem';
import { RailgunChargeLogicSystem } from '@renderSystems/RailgunChargeLogicSystem';
import { EffectRequestSystem } from '@systems/EffectRequestSystem';
import { UIUpdateSystem } from '@renderSystems/UIUpdateSystem';
import { EntityFactory } from '@entities/EntityFactory';
import { WeaponMode } from '@/types';
import { Tag } from '@components/Tag';
import { Velocity } from '@components/Velocity';
import { getInputPos } from '@/utils';
import { LevelConfig } from '@/levels/LevelData';
import { SoundManager } from '@audio/SoundManager';
import { EnemyBoundarySystem } from '@systems/EnemyBoundarySystem';
import { FixedTimestep } from '@/core/FixedTimestep';
import { applyWeaponMode } from '@utils/WeaponModeUtils';
import { getFormationOffsets } from '@utils/Formations';
import { CirclerAISystem } from '@systems/CirclerAISystem';
import { MineLayerSystem } from '@systems/MineLayerSystem';
import { CarrierSystem } from '@systems/CarrierSystem';
import { HitFlashSystem } from '@systems/HitFlashSystem';
import { CameraShakeSystem } from '@systems/CameraShakeSystem';
import { pickEnemyType, spawnPointOnEdge } from '@/levels/EnemySpawner';
import { CommandController } from '@/input/CommandController';
import { AttackOrderSystem } from '@systems/AttackOrderSystem';
import { ShieldProjectorSystem } from '@systems/ShieldProjectorSystem';
import { ReinforcementTracker } from '@/levels/ReinforcementTracker';
import { RosterLink } from '@components/RosterLink';
import { Selectable } from '@components/Selectable';
import { StandardWeapon } from '@components/StandardWeapon';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Effects } from '@/effects/Effects';
import { PLAYER_SHIP_INFO } from '@entities/PlayerShips';
import { FleetBar } from '@/ui/FleetBar';
import { DamageSmokeSystem } from '@systems/DamageSmokeSystem';
import { MissionReport, RosterShip } from '@/fleet/Fleet';
import { buildMissionReport, createRosterShipEntity } from '@/fleet/MissionRoster';
import { BaseArrival, BaseScene } from '@/base/BaseScene';

// ECS World
const world = new World();

// Sound Manager
const soundManager = SoundManager.getInstance();

// Get the global level manager instance
const levelManager = (window as any).levelManager;

// Systems
const movementSystem = new MovementSystem();
const lifetimeSystem = new LifetimeSystem();
const renderSystem = new PureRenderSystem();
const inputSystem = new InputSystem();
const standardWeaponSystem = new StandardWeaponSystem();
const railgunWeaponSystem = new RailgunWeaponSystem();
const torpedoWeaponSystem = new TorpedoWeaponSystem();
const torpedoSystem = new TorpedoSystem();
const pointDefenseSystem = new PointDefenseSystem();
const energyRegenSystem = new EnergyRegenSystem();
const shieldSystem = new ShieldSystem();
const energyUISystem = new EnergyUISystem();
const hunterAISystem = new HunterAISystem();
// New logic systems
const targetingLogicSystem = new TargetingLogicSystem();
const weaponRangeLogicSystem = new WeaponRangeLogicSystem();
const healthDisplayLogicSystem = new HealthDisplayLogicSystem();
const shieldLogicSystem = new ShieldLogicSystem();
const railgunChargeLogicSystem = new RailgunChargeLogicSystem();
const collisionLogicSystem = new CollisionLogicSystem();
const collisionEffectSystem = new CollisionEffectSystem();
const gameStateSystem = new GameStateSystem();
const effectRequestSystem = new EffectRequestSystem();
const uiUpdateSystem = new UIUpdateSystem();
const sniperAISystem = new SniperAISystem();
const particleSystem = new ParticleSystem();
const separationSystem = new SeparationSystem();
const enemyBoundarySystem = new EnemyBoundarySystem();
const circlerAISystem = new CirclerAISystem();
const mineLayerSystem = new MineLayerSystem();
const carrierSystem = new CarrierSystem();
const hitFlashSystem = new HitFlashSystem();
const cameraShakeSystem = new CameraShakeSystem();
const attackOrderSystem = new AttackOrderSystem();
const shieldProjectorSystem = new ShieldProjectorSystem();
const damageSmokeSystem = new DamageSmokeSystem();
const commands = new CommandController(world);
const fleetBar = new FleetBar(world, commands);
let reinforcements = new ReinforcementTracker<RosterShip>(0, []);
// Roster ids of every ship that has flown in the current mission
let participants: number[] = [];
let baseScene: BaseScene | null = null;
const STEP_MS = 16;
const clock = new FixedTimestep(1000 / 60);

// Canvas and context
let canvas: HTMLCanvasElement;
let ctx: CanvasRenderingContext2D;

// Game state
let gameRunning = false;
let lastEnemySpawn = Date.now();
let frameCount = 0;
let currentLevelConfig: LevelConfig | null = null;
let animationFrameId: number | null = null;

// Input handling
function pointerFrom(e: MouseEvent | TouchEvent) {
  const pos = getInputPos(e);
  const isTouch = 'touches' in e;
  return {
    x: pos.x,
    y: pos.y,
    button: (!isTouch && (e as MouseEvent).button === 2 ? 'secondary' : 'primary') as 'primary' | 'secondary',
    shift: e.shiftKey,
    touch: isTouch,
  };
}

function handleInputStart(e: MouseEvent | TouchEvent): void {
  e.preventDefault();
  if (!gameRunning) return;
  commands.pointerDown(pointerFrom(e));
}

function handleInputMove(e: MouseEvent | TouchEvent): void {
  if (!canvas || !gameRunning) return;
  const pos = getInputPos(e);
  commands.pointerMove(pos.x, pos.y);
}

function handleInputEnd(e: MouseEvent | TouchEvent): void {
  if (!gameRunning) return;
  // Only finish gestures that started on the canvas
  if (!('touches' in e) && e.target !== canvas && !commands.isDragging()) return;
  commands.pointerUp(pointerFrom(e));
  syncModeDisplay();
}

// Weapon mode
export function setWeaponMode(mode: WeaponMode): void {
  // Applies to the selected ships, or the whole fleet when nothing is selected
  applyWeaponMode(world, mode, commands.getSelected());
  syncModeDisplay();
}

/** Shows the fire mode of the selected ships (or the whole fleet). */
function syncModeDisplay(): void {
  const ships = commands.getSelected().length > 0
    ? commands.getSelected()
    : world.getEntitiesWithComponents(Tag, StandardWeapon).filter(e => e.getComponent(Tag)!.value === 'player');
  const modes = new Set(ships.map(ship => ship.getComponent(StandardWeapon)!.mode));
  const mode = modes.size === 1 ? [...modes][0] : null;

  const currentModeElement = document.getElementById('currentMode');
  if (currentModeElement) {
    currentModeElement.textContent = mode ? mode.toUpperCase() : 'MIXED';
  }

  document.querySelectorAll('.weapon-btn').forEach(btn => btn.classList.remove('active'));
  if (mode) document.getElementById(mode + 'Btn')?.classList.add('active');
}

// Spawn enemies
function spawnEnemy(): void {
  if (!currentLevelConfig) return;

  const margin = 50; // Spawn distance outside viewport
  const { x, y } = spawnPointOnEdge(canvas.width, canvas.height, margin, Math.random(), Math.random());

  // Calculate initial angle towards viewport center
  const initialAngle = Math.atan2(canvas.height / 2 - y, canvas.width / 2 - x);

  const type = pickEnemyType(currentLevelConfig.enemyWeights, Math.random());
  for (const enemy of EntityFactory.createEnemyOfType(type, x, y)) {
    // Set initial velocity towards viewport
    const velocity = enemy.getComponent(Velocity);
    if (velocity) {
      velocity.angle = initialAngle;
      velocity.speed = velocity.maxSpeed; // Start at full speed
    }
    world.addEntity(enemy);
  }
}

// Game loop
function scheduleNextFrame(): void {
  animationFrameId = requestAnimationFrame(gameLoop);
}

function stopLoop(): void {
  gameRunning = false;
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}

function countTagged(value: string): number {
  return world.getEntitiesWithComponents(Tag).filter(e => e.getComponent(Tag)!.value === value).length;
}

function simulateStep(): void {
  // Update world with fixed timestep
  world.update(STEP_MS);
  if (!gameRunning) return;

  // Survival objectives are measured in simulated time
  levelManager.onTick(STEP_MS);
  if (!gameRunning) return;

  // Spawn enemies based on level config
  if (currentLevelConfig) {
    const spawnRate = currentLevelConfig.enemySpawnRate;
    const maxEnemies = currentLevelConfig.maxEnemies;

    if (Date.now() - lastEnemySpawn > spawnRate && countTagged('enemy') < maxEnemies) {
      spawnEnemy();
      lastEnemySpawn = Date.now();
    }
  }
}

function gameLoop(now: number = performance.now()): void {
  animationFrameId = null;
  if (!gameRunning) return;

  // Run as many fixed steps as real time demands so speed is refresh-rate independent
  const steps = clock.advance(now);
  for (let i = 0; i < steps && gameRunning; i++) {
    simulateStep();
  }

  // Update HUD a few times per second
  if (frameCount++ % 6 === 0) updateHud();

  if (gameRunning) scheduleNextFrame();
}

function updateHud(): void {
  const enemyCountElement = document.getElementById('enemyCount');
  if (enemyCountElement) enemyCountElement.textContent = countTagged('enemy').toString();

  const weaponRangeElement = document.getElementById('weaponRange');
  const rangeSource = commands.getSelected()[0]?.getComponent(StandardWeapon);
  if (weaponRangeElement) weaponRangeElement.textContent = rangeSource ? rangeSource.range.toString() : '-';

  const bulletCountElement = document.getElementById('bulletCount');
  if (bulletCountElement) bulletCountElement.textContent = countTagged('bullet').toString();

  const reinforcementElement = document.getElementById('reinforcementInfo');
  if (reinforcementElement) {
    const kills = reinforcements.killsUntilNext();
    reinforcementElement.textContent = kills === null ? '' : `Reinforcement in ${kills} kill${kills === 1 ? '' : 's'}`;
    reinforcementElement.style.display = kills === null ? 'none' : 'block';
  }

  fleetBar.render();
}

/** Warps in a reserve ship from the roster to replace a loss. */
function spawnReinforcement(reserveShip: RosterShip): void {
  const fleet = world.getEntitiesWithComponents(Tag, Selectable, Position)
    .filter(e => e.getComponent(Tag)!.value === 'player' && !world.isPendingRemoval(e));
  const wholeFleetSelected = fleet.length > 0 && fleet.every(ship => ship.getComponent(Selectable)!.selected);

  // Warp in beside the fleet, or at the centre if it's gone
  let x = canvas.width / 2;
  let y = canvas.height / 2;
  if (fleet.length > 0) {
    x = fleet.reduce((sum, s) => sum + s.getComponent(Position)!.x, 0) / fleet.length;
    y = fleet.reduce((sum, s) => sum + s.getComponent(Position)!.y, 0) / fleet.length + 70;
  }
  x = Math.min(canvas.width - 40, Math.max(40, x));
  y = Math.min(canvas.height - 40, Math.max(40, y));

  const ship = createRosterShipEntity(reserveShip, x, y);
  participants.push(reserveShip.id);
  ship.getComponent(Velocity)!.angle = -Math.PI / 2;
  ship.getComponent(Selectable)!.selected = wholeFleetSelected;
  world.addEntity(ship);
  Effects.warpIn(world, x, y, PLAYER_SHIP_INFO[reserveShip.shipClass].color);
}

// Start game with level, flying the deployed roster ships and holding the rest in reserve
export function startGameWithLevel(levelConfig: LevelConfig, deployed: RosterShip[], reserve: RosterShip[]): void {
  currentLevelConfig = levelConfig;
  baseScene?.hide();

  // Make sure a previous run's loop can't keep ticking alongside the new one
  stopLoop();

  // Clear world
  world.clear();
  gameStateSystem.reset();

  // Reset collision system stats
  collisionLogicSystem.resetStats();
  gameStateSystem.setStatsProvider(() => ({
    score: collisionLogicSystem.getScore(),
    enemiesKilled: collisionLogicSystem.getEnemiesKilled()
  }));

  // Launch the deployed roster ships in formation, carrying their hull damage
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const fleet = deployed.slice(0, levelConfig.deploymentLimit);
  const formation = getFormationOffsets(fleet.length);

  fleet.forEach((ship, i) => {
    const pos = formation[i];
    world.addEntity(createRosterShipEntity(ship, centerX + pos.x, centerY + pos.y));
  });
  participants = fleet.map(ship => ship.id);

  // Start with the whole fleet selected
  commands.reset();
  commands.selectAll();
  reinforcements = new ReinforcementTracker(levelConfig.reinforceEvery, reserve);

  // Reset game state
  gameRunning = true;
  lastEnemySpawn = Date.now();

  // Reset UI
  const scoreElement = document.getElementById('score');
  if (scoreElement) scoreElement.textContent = '0';


  const energyElement = document.getElementById('energy');
  if (energyElement) energyElement.textContent = '100';

  const gameOverElement = document.getElementById('gameOver');
  if (gameOverElement) gameOverElement.style.display = 'none';

  setWeaponMode('auto');

  // Set up enemy killed callback
  collisionLogicSystem.setEnemyKilledCallback(() => {
    levelManager.onEnemyKilled();
    const reinforcement = reinforcements.onKill();
    if (reinforcement && gameRunning) spawnReinforcement(reinforcement);
  });

  collisionLogicSystem.setPlayerShipLostCallback((ship: Entity) => {
    if (ship.hasComponent(RosterLink)) reinforcements.onShipLost();
  });

  gameStateSystem.setGameEndCallback((isVictory: boolean) => {
    gameRunning = false;
    if (!isVictory) levelManager.onMissionFailed();
  });

  updateHud();
  clock.reset(performance.now());
  scheduleNextFrame();
}

/** Which roster ships came home, and with how much hull, for the mission just played. */
export function getMissionReport(completed: boolean): MissionReport {
  return buildMissionReport(world, participants, collisionLogicSystem.getScore(), completed);
}

/** Lands on the mothership, optionally bringing a mission's earnings and losses home. */
export function showBase(arrival: BaseArrival | null = null): void {
  stopGame();
  baseScene?.show(arrival);
}

// Stop game
export function stopGame(): void {
  stopLoop();
  gameStateSystem.stop();
}

// Reset game
export function resetGame(): void {
  // Restart through the level manager so objective progress resets too
  levelManager.restartLevel();
}

// Resize canvas to window size
function resizeCanvas(): void {
  if (!canvas) return;

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  enemyBoundarySystem.setBounds(canvas.width, canvas.height);
  baseScene?.resize();

}

// Initialize game
async function init(): Promise<void> {
  canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
  ctx = canvas.getContext('2d')!;

  // Set initial canvas size
  resizeCanvas();

  // Handle window resize
  window.addEventListener('resize', resizeCanvas);
  
  // Initialize sound manager on first user interaction
  const initSoundOnInteraction = async () => {
    try {
      await soundManager.initialize();
      // Remove listeners after successful initialization
      document.removeEventListener('click', initSoundOnInteraction);
      document.removeEventListener('touchstart', initSoundOnInteraction);
    } catch (error) {
      console.warn('Failed to initialize sound:', error);
    }
  };
  
  // Add listeners for first user interaction
  document.addEventListener('click', initSoundOnInteraction);
  document.addEventListener('touchstart', initSoundOnInteraction);

  // Initialize systems
  renderSystem.setContext(ctx);
  standardWeaponSystem.setCanvas(canvas);
  railgunWeaponSystem.setCanvas(canvas);
  torpedoWeaponSystem.setCanvas(canvas);
  mineLayerSystem.setCanvas(canvas);
  carrierSystem.setCanvas(canvas);

  // Add systems to world in proper order
  // Phase 1: Input and AI
  world.addSystem(attackOrderSystem);
  world.addSystem(inputSystem);
  world.addSystem(hunterAISystem);
  world.addSystem(sniperAISystem);
  world.addSystem(circlerAISystem);
  world.addSystem(enemyBoundarySystem);
  
  // Phase 2: Movement and physics
  world.addSystem(movementSystem);
  world.addSystem(separationSystem);
  
  // Phase 3: Logic systems
  world.addSystem(targetingLogicSystem);
  world.addSystem(weaponRangeLogicSystem);
  world.addSystem(healthDisplayLogicSystem);
  world.addSystem(shieldLogicSystem);
  world.addSystem(railgunChargeLogicSystem);
  
  // Phase 4: Weapon systems
  world.addSystem(standardWeaponSystem);
  world.addSystem(railgunWeaponSystem);
  world.addSystem(torpedoWeaponSystem);
  world.addSystem(torpedoSystem);
  world.addSystem(pointDefenseSystem);
  world.addSystem(mineLayerSystem);
  world.addSystem(carrierSystem);
  
  // Phase 5: Energy and shield logic
  world.addSystem(energyRegenSystem);
  world.addSystem(shieldSystem);
  world.addSystem(shieldProjectorSystem);
  world.addSystem(energyUISystem);
  world.addSystem(damageSmokeSystem);
  
  // Phase 6: Collision detection and effects
  world.addSystem(collisionLogicSystem);
  world.addSystem(collisionEffectSystem);
  world.addSystem(effectRequestSystem);
  
  // Phase 7: Game state and UI
  world.addSystem(gameStateSystem);
  world.addSystem(uiUpdateSystem);
  
  // Phase 8: Cleanup and rendering
  world.addSystem(lifetimeSystem);
  world.addSystem(particleSystem);
  world.addSystem(hitFlashSystem);
  world.addSystem(cameraShakeSystem);
  world.addSystem(renderSystem); // Render last

  // Set up event listeners
  canvas.addEventListener('mousedown', handleInputStart);
  canvas.addEventListener('touchstart', handleInputStart);
  window.addEventListener('mousemove', handleInputMove);
  canvas.addEventListener('touchmove', (e) => { e.preventDefault(); handleInputMove(e); }, { passive: false });
  window.addEventListener('mouseup', handleInputEnd);
  canvas.addEventListener('touchend', (e) => { e.preventDefault(); handleInputEnd(e); });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  // Keyboard: fleet commands, then weapon modes
  const modeKeys: Record<string, WeaponMode> = { q: 'auto', w: 'manual', e: 'defensive' };
  window.addEventListener('keydown', (e) => {
    if (!gameRunning) return;
    if (commands.handleKey(e.key, { ctrl: e.ctrlKey, meta: e.metaKey, shift: e.shiftKey })) {
      e.preventDefault();
      syncModeDisplay();
      updateHud();
      return;
    }
    const mode = modeKeys[e.key.toLowerCase()];
    if (mode && !e.ctrlKey && !e.metaKey) setWeaponMode(mode);
  });

  // Set up button click handlers
  (window as any).setWeaponMode = setWeaponMode;
  fleetBar.setSelectionChangeHandler(syncModeDisplay);
  (window as any).selectAllShips = () => { commands.selectAll(); syncModeDisplay(); updateHud(); };
  (window as any).resetGame = resetGame;
  (window as any).startGameWithLevel = startGameWithLevel;
  (window as any).stopGame = stopGame;
  (window as any).getMissionReport = getMissionReport;
  (window as any).showBase = showBase;
  
  // Set up sound controls; the HUD and the mothership each have a set, kept in sync
  const volumeSliders = ['volumeSlider', 'baseVolumeSlider']
    .map(id => document.getElementById(id) as HTMLInputElement | null)
    .filter((slider): slider is HTMLInputElement => slider !== null);
  const muteButtons = ['muteBtn', 'baseMuteBtn']
    .map(id => document.getElementById(id) as HTMLButtonElement | null)
    .filter((button): button is HTMLButtonElement => button !== null);

  volumeSliders.forEach(slider => slider.addEventListener('input', () => {
    soundManager.setVolume(parseInt(slider.value) / 100);
    volumeSliders.forEach(other => { other.value = slider.value; });
  }));

  muteButtons.forEach(button => button.addEventListener('click', () => {
    soundManager.setEnabled(!soundManager.isEnabled());
    muteButtons.forEach(other => { other.textContent = soundManager.isEnabled() ? '🔊' : '🔇'; });
  }));

  // Hide game UI initially
  const gameContainer = document.querySelector('.game-container') as HTMLElement;
  if (gameContainer) gameContainer.style.display = 'block';

  // Land on the mothership
  baseScene = new BaseScene(canvas, levelManager.getProgress(), () => levelManager.returnToLevelSelect());
  showBase();
}

// Start the game when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}